// @flow
import {
  getCard,
  getCards,
  removeCard,
  saveCard,
  setDefaultCard,
  updateCard,
} from '@rover/react-lib/apis/creditCards.api';
import CreditCardForm, {
  type Props as CreditCardFormProps,
} from '@rover/react-lib/components/forms/CreditCardForm';
import {
  type ReduxLiftedComponentClass,
  lifted,
} from '@rover/react-lib/lib/lifted-components/redux';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/lib/redux-duck';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type {
  CreditCardAddSummary,
  CreditCardSummary,
  CreditCardUpdateSummary,
} from '@rover/react-lib/types';
import { isMobileEmbedded } from '@rover/utilities/userAgent';

export const LiftedCreditCardForm = (lifted(CreditCardForm): ReduxLiftedComponentClass<
  CreditCardFormProps,
  { addingCard: boolean, isEditingCard: boolean }
>);

export type State = {
  cards: Array<CreditCardSummary>,
  errorMessage: string,
  loading: boolean,
  cardToUpdate: CreditCardSummary,
};

type Actions = {
  creditCardsPending: ActionCreator<void>,
  creditCardsFailure: ActionCreator<{ errorMessage: string }>,
  fetchCreditCardsSuccess: ActionCreator<{ cards: Array<CreditCardSummary> }>,
  fetchCreditCardSuccess: ActionCreator<{ creditCard: CreditCardSummary }>,
  setDefaultCreditCardSuccess: ActionCreator<{ creditCard: CreditCardSummary }>,
  removeCreditCardSuccess: ActionCreator<{ cardUrl: string }>,
  createCreditCardAttempt: ActionCreator<?{ errorMessage: string }>,
  updateCreditCardAttempt: ActionCreator<?{ errorMessage: string }>,
  updateEditingCard: ActionCreator<CreditCardSummary>,
};

type Effects = {
  fetchCreditCards: EffectCreator<void>,
  fetchCreditCard: EffectCreator<string>,
  setDefaultCreditCard: EffectCreator<string>,
  removeCreditCard: EffectCreator<string>,
  updateCreditCard: EffectCreator<CreditCardUpdateSummary | CreditCardAddSummary>,
};

type Selectors = {
  getCreditCards: SelectorCreator<Array<CreditCardSummary>>,
  getDefaultCreditCard: SelectorCreator<CreditCardSummary>,
  getDefaultCreditCardId: SelectorCreator<string>,
  getErrorMessage: SelectorCreator<string>,
  getLoading: SelectorCreator<boolean>,
  isAddingCard: SelectorCreator<boolean>,
  isEditingCard: SelectorCreator<boolean>,
  getCardToUpdate: SelectorCreator<CreditCardSummary>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'creditCards',
  initialState: {
    cards: [],
    errorMessage: '',
    loading: false,
    cardToUpdate: {},
  },
  actions: {
    creditCardsPending: () => (state): State => ({
      ...state,
      loading: true,
      errorMessage: '',
    }),
    creditCardsFailure: ({ errorMessage }) => (state): State => ({
      ...state,
      loading: false,
      errorMessage,
    }),
    fetchCreditCardsSuccess: ({ cards }) => (state): State => ({
      ...state,
      cards,
      errorMessage: '',
      loading: false,
    }),
    fetchCreditCardSuccess: ({ creditCard }) => (state): State => ({
      ...state,
      cards: [...state.cards.filter(c => c.url !== creditCard.url), creditCard],
      errorMessage: '',
      loading: false,
    }),
    setDefaultCreditCardSuccess: ({ creditCard }) => (state): State => ({
      ...state,
      cards: [
        ...state.cards.map(c => ({
          ...c,
          defaultPayment: c.url === creditCard.url,
        })),
      ],
      errorMessage: '',
      loading: false,
    }),
    removeCreditCardSuccess: ({ cardUrl }) => (state): State => ({
      ...state,
      cards: state.cards.filter(card => card.url !== cardUrl),
      errorMessage: '',
      loading: false,
    }),
    createCreditCardAttempt: (opts?) => (state): State => ({
      ...state,
      loading: false,
      errorMessage: opts?.errorMessage || '',
    }),
    updateCreditCardAttempt: (opts?) => (state): State => ({
      ...state,
      loading: false,
      errorMessage: opts?.errorMessage || '',
    }),
    updateEditingCard: cardToUpdate => (state): State => ({
      ...state,
      cardToUpdate,
    }),
  },
  effects: {
    fetchCreditCards: () => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, fetchCreditCardsSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const cards = await getCards();
        dispatch(fetchCreditCardsSuccess({ cards }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    fetchCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, fetchCreditCardSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const creditCard = await getCard(cardUrl);
        dispatch(fetchCreditCardSuccess({ creditCard }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    setDefaultCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, setDefaultCreditCardSuccess }
    ): Promise<boolean> => {
      dispatch(creditCardsPending());

      try {
        const creditCard = await setDefaultCard(cardUrl);
        dispatch(setDefaultCreditCardSuccess({ creditCard }));
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
    removeCreditCard: (cardUrl: string) => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, removeCreditCardSuccess },
      { fetchCreditCards }
    ): Promise<void> => {
      dispatch(creditCardsPending());

      try {
        await removeCard(cardUrl);
        dispatch(removeCreditCardSuccess({ cardUrl }));
        dispatch(fetchCreditCards());
      } catch (error) {
        dispatch(creditCardsFailure({ errorMessage: error.message }));
      }
    },
    updateCreditCard: data => async (
      dispatch,
      getState,
      { creditCardsPending, creditCardsFailure, createCreditCardAttempt, updateCreditCardAttempt },
      { fetchCreditCards },
      { getLoading, isEditingCard, getCardToUpdate }
    ): Promise<boolean> => {
      // note: we need separate saveCreditCardAttempts for the analytics
      const isEdit = isEditingCard(getState());
      const cardAttempt = isEdit ? updateCreditCardAttempt : createCreditCardAttempt;
      // prevent submission if one is already active
      if (getLoading(getState())) {
        dispatch(cardAttempt({ errorMessage: 'Network request already in progress' }));
        return false;
      }
      dispatch(creditCardsPending());
      try {
        if (isEdit) {
          const { url } = getCardToUpdate(getState());
          await updateCard(url, data);
        } else {
          await saveCard(data);
          if (isMobileEmbedded()) {
            // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
            window.location.hash = 'payment-method-added';
          }
        }
        await dispatch(fetchCreditCards());
        dispatch(cardAttempt());
      } catch (error) {
        dispatch(cardAttempt({ errorMessage: error.message }));
        dispatch(creditCardsFailure({ errorMessage: error.message }));
        return false;
      }
      return true;
    },
  },
  selectors: {
    getCreditCards: (getState, createSelector) =>
      createSelector([getState], s => {
        const NOW = new Date();
        const curMonth = NOW.getMonth() + 1;
        const curYear = NOW.getFullYear();
        return s.cards.filter(card => {
          const cardYear = parseInt(card.expirationYear, 10);
          const cardMonth = parseInt(card.expirationMonth, 10);
          if (cardYear < curYear) {
            return false;
          }
          if (cardYear === curYear && cardMonth < curMonth) {
            return false;
          }

          return true;
        });
      }),

    getDefaultCreditCard: (getState, createSelector, { getCreditCards }) =>
      createSelector([getCreditCards], cards => cards.find(c => c.defaultPayment) || cards[0]),

    getDefaultCreditCardId: (getState, createSelector, { getDefaultCreditCard }) =>
      createSelector([getDefaultCreditCard], defaultCard => (defaultCard ? defaultCard.url : '')),

    getErrorMessage: (getState, createSelector) => createSelector([getState], s => s.errorMessage),

    getLoading: (getState, createSelector) => createSelector([getState], s => s.loading),
    isAddingCard: () => (state: State) => LiftedCreditCardForm.select(state).addingCard,
    isEditingCard: () => (state: State) => LiftedCreditCardForm.select(state).isEditingCard,
    getCardToUpdate: (getState, createSelector) => createSelector([getState], s => s.cardToUpdate),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/creditCards.duck.js